// =============================
// ÍNDICE DO ARQUIVO
// 15: ClientList (componente principal)
// 24: useEffect - Carregamento de clientes
// 44: handleEdit
// 49: handleDialogClose
// 54: handleSaveEdit
// 73: handleOpenDeleteDialog
// 78: handleCloseDeleteDialog
// 83: handleConfirmDelete
// =============================

import React, { useState, useEffect } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import ClearIcon from "@mui/icons-material/Clear"
import { supabase, supabaseAdminClient } from "../../Supabase/supabaseRealtimeClient"
import ConfirmDeleteDialog from "../../components/ConfirmDeleteDialog"
import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import DependenteListDialog from "./DependenteListDialog"

interface Cliente {
  id: number
  user_id: string
  nome: string
  email: string
  telefone: string
  status: string
  tipo_plano: "basico" | "premium" | "saude24h"
  perfil: string
  criado_em?: string
  deletado?: boolean
  dispositivo?: string
}

interface ClientListProps {
  onEditClient: (cliente: Cliente) => void
}

const ClientList: React.FC<ClientListProps> = ({ onEditClient }) => {
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [clientToDelete, setClientToDelete] = useState<Cliente | null>(null)
  const [depListOpen, setDepListOpen] = useState(false)
  const [selectedTitular, setSelectedTitular] = useState<Cliente | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  const filteredClientes = clientes.filter(cliente => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      cliente.nome?.toLowerCase().includes(query) ||
      cliente.email?.toLowerCase().includes(query) ||
      cliente.telefone?.toLowerCase().includes(query) ||
      cliente.status?.toLowerCase().includes(query) ||
      cliente.tipo_plano?.toLowerCase().includes(query) ||
      cliente.id?.toString().includes(query) ||
      cliente.perfil?.toLowerCase().includes(query)
    )
  })

  // Função para buscar clientes via API
  const fetchClientesFromAPI = async () => {
    try {
      setLoading(true)
      setError(null)
      const url = process.env.REACT_APP_SUPABASE_URL
      const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY
      if (!url || !serviceKey)
        throw new Error(
          "REACT_APP_SUPABASE_URL ou REACT_APP_SUPABASE_SERVICE_KEY não definida no .env"
        )
      const accessToken = localStorage.getItem("accessToken")

      const response = await fetch(`${url}/rest/v1/cliente`, {
        method: "GET",
        headers: {
          apikey: serviceKey,
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json"
        }
      })

      let data = await response.json()
      if (!response.ok)
        throw new Error(
          data.error_description || data.message || `Erro ${response.status}`
        )

      // Filtrar clientes não deletados
      const clientesNaoDeletados = data
        .filter((c: any) => !c.deletado)

      // Ordena do mais recente para o mais antigo
      clientesNaoDeletados.sort((a: Cliente, b: Cliente) => {
        if (a.criado_em && b.criado_em) {
          return (
            new Date(b.criado_em).getTime() - new Date(a.criado_em).getTime()
          )
        }
        return b.id - a.id
      })

      setClientes(clientesNaoDeletados)
    } catch (error) {
      console.error("Erro ao carregar clientes:", error)
      setError(
        "Não foi possível carregar a lista de clientes. Por favor, tente novamente mais tarde."
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchClientesFromAPI()
    const channel = supabase
      .channel("public:cliente")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cliente" },
        payload => {
          fetchClientesFromAPI()
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  const handleOpenDeleteDialog = (cliente: Cliente) => {
    setClientToDelete(cliente)
    console.log(cliente)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setClientToDelete(null)
  }

  const handleOpenDepList = (cliente: Cliente) => {
    setSelectedTitular(cliente)
    setDepListOpen(true)
  }

  const handleCloseDepList = () => {
    setDepListOpen(false)
    setSelectedTitular(null)
  }

  const handleConfirmDelete = async () => {
    if (!clientToDelete) return
    try {
      setLoading(true)
      
      console.log('[ClientList] Iniciando exclusão do cliente:', clientToDelete.id);

      // 1. Marcar como deletado na tabela cliente (Soft Delete) e liberar o e-mail
      // Usamos o supabaseAdminClient para garantir permissão total (contornar RLS)
      const { error: deleteTableError } = await supabaseAdminClient
        .from('cliente')
        .update({
          deletado: true,
          status: 'inativo',
          email: `deletado_${clientToDelete.id}@deleted.com`
        })
        .eq('id', clientToDelete.id);

      if (deleteTableError) {
        console.error('[ClientList] Erro ao atualizar a tabela cliente:', deleteTableError);
        throw deleteTableError;
      }

      // 2. Excluir fisicamente do Supabase Auth para revogar acessos e liberar o e-mail no GoTrue
      if (clientToDelete.user_id) {
        console.log('[ClientList] Excluindo usuário do Auth:', clientToDelete.user_id);
        const { error: deleteAuthError } = await supabaseAdminClient.auth.admin.deleteUser(
          clientToDelete.user_id.toString()
        );

        if (deleteAuthError) {
          console.warn('[ClientList] Aviso: Erro ao excluir do Auth (pode já não existir):', deleteAuthError);
          // Não lançamos erro aqui para não travar o fluxo se o usuário já tiver sido removido do Auth manualmente
        }
      }

      setDeleteDialogOpen(false)
      setClientToDelete(null)
      alert("Cliente excluído com sucesso!")
      // Recarregar lista
      fetchClientesFromAPI()
    } catch (error: any) {
      console.error('[ClientList] Erro fatal na exclusão:', error);
      alert(`Erro ao excluir cliente: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>{error}</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Pesquisar por nome, email, telefone, status, plano, ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchQuery("")} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }
          }}
        />
      </Box>
      <TableContainer
        component={Paper}
        sx={{ overflowX: "auto", maxWidth: "100vw" }}
      >
        <Table aria-label='tabela de clientes'>
          <TableHead>
            <TableRow>
              <TableCell sx={!isDesktop ? { width: "33.33%" } : {}}>ID</TableCell>
              <TableCell sx={!isDesktop ? { width: "33.33%" } : {}}>
                Nome
              </TableCell>
              {isDesktop && <TableCell>Email</TableCell>}
              {isDesktop && <TableCell>Telefone</TableCell>}
              {isDesktop && <TableCell>Status</TableCell>}
              <TableCell
                sx={
                  !isDesktop
                    ? { width: "33.33%", minWidth: 100, whiteSpace: "nowrap" }
                    : { minWidth: 100, whiteSpace: "nowrap" }
                }
              >
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClientes.length > 0 ? (
              filteredClientes.map(cliente => (
                <TableRow key={cliente.id}>
                  <TableCell sx={!isDesktop ? { width: "33.33%" } : {}}>
                    {cliente.id}
                  </TableCell>
                  <TableCell sx={!isDesktop ? { width: "33.33%" } : {}}>
                    {cliente.nome}
                  </TableCell>
                  {isDesktop && <TableCell>{cliente.email}</TableCell>}
                  {isDesktop && <TableCell>{cliente.telefone}</TableCell>}
                  {isDesktop && <TableCell>{cliente.status}</TableCell>}
                  <TableCell
                    sx={
                      !isDesktop
                        ? { width: "33.33%", minWidth: 100, whiteSpace: "nowrap" }
                        : { minWidth: 100, whiteSpace: "nowrap" }
                    }
                  >
                    <Button
                      size='small'
                      color='primary'
                      onClick={() => onEditClient(cliente)}
                    >
                      Editar
                    </Button>
                    <Button
                      size='small'
                      sx={{ color: '#4caf50' }}
                      onClick={() => handleOpenDepList(cliente)}
                    >
                      Dependentes
                    </Button>
                    <Button
                      size='small'
                      color='error'
                      onClick={() => handleOpenDeleteDialog(cliente)}
                    >
                      Excluir
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={isDesktop ? 6 : 3}
                  align='center'
                  sx={
                    !isDesktop
                      ? { width: "33.33%" }
                      : { minWidth: 100, whiteSpace: "nowrap" }
                  }
                >
                  Nenhum cliente encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        itemName={clientToDelete?.nome}
      />
      <DependenteListDialog
        open={depListOpen}
        onClose={handleCloseDepList}
        titular={selectedTitular}
      />
    </Box>
  )
}

export default ClientList

